/**
 * Строка списка процессов + пилюля укрупнённого статуса. Одна и та же строка рисуется на
 * /processes и в карточке «Все процессы» на главной.
 *
 * Состав данных — макет «Список процессов — новая карточка» (29.09.2026): слева название, компания,
 * контрагент (договор) / детали (прочие виды), под ними номер и автор; справа — «Сейчас ожидает»,
 * статус с прогрессом и шеврон. Кегли, плашки и отступы — портала, НЕ макета (замечание 29.09.2026:
 * макет задаёт, что показывать, а не как оно выглядит).
 *
 * Макет «Процессы — фильтр» (16.10.2026): у каждой строки метка моей роли («Вы автор / согласующий /
 * подписант», rolesOf), а строка с открытой задачей на мне выделена рамкой и «Ваше решение».
 */
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import { initials } from '../../../lib/avatar'
import { useResponsive } from '../../../lib/responsive'
import { tone, type Tone } from '../../../theme/tones'
import { Icon, PressCard } from '../../../ui'
import { PROCESS_STATUS, typeOf, type ProcStatus } from '../registry'
import { type ProcVM, docNo, currentStep, rolesOf, PARTICIPANT_ROLE_LABEL } from '../processVM'

// ── Status pill ──────────────────────────────────────────────────────────────
export function StatusPill({ status, label }: { status: ProcStatus; label?: Localized }) {
  const { t } = useApp()
  const st = PROCESS_STATUS[status]
  const c = tone(st.tone)
  return (
    <XStack backgroundColor={c.soft} borderRadius={999} paddingHorizontal={11} paddingVertical={5} alignSelf="flex-start">
      <Text fontSize={11} fontWeight="700" color={c.text} numberOfLines={1}>
        {t(label ?? st.label)}
      </Text>
    </XStack>
  )
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <XStack backgroundColor="$primaryBg" borderRadius={999} paddingHorizontal={8} paddingVertical={2}>
      <Text fontSize={10} fontWeight="700" color="$primary" numberOfLines={1}>
        {children}
      </Text>
    </XStack>
  )
}

// ── Process list row ──────────────────────────────────────────────────────────
export function ProcessRow({ p, onOpen }: { p: ProcVM; onOpen: (p: ProcVM) => void }) {
  const { t, l } = useApp()
  // Плотность — по ширине окна с запасом на развёрнутый сайдбар (236) и поля страницы: карточка
  // ≈ min(1280, окно − 236) − 48. Мерить саму карточку (onLayout) нельзя: на вебе Tamagui меряет
  // только видимые узлы, строки ниже экрана перестраивались бы на глазах при прокрутке.
  const { width } = useResponsive()
  const stacked = width < 820 // карточка уже ~540: правый блок уходит под текст
  const showWait = width >= 1024 // карточка от ~740: помещается «Сейчас ожидает»

  const type = typeOf(p.type)
  const tnType = tone(type.tone)
  const tnStatus = tone(PROCESS_STATUS[p.status].tone)
  const pct = p.approvalRouteUnavailable ? 0 : Math.round((p.step / p.steps) * 100)
  const cur = currentStep(p)
  // Открытая задача на мне: строка выделена, в «Сейчас» — «Ваше решение» (тон «в работе»).
  const needsMe = !!p.task && !!p.canDecide
  const tnDecide = tone('gold')

  // Заголовок: конкретное наименование (p.title, см. titleOf в processVM); группа и детали —
  // следом, только если отличаются от заголовка (иначе «Отпуск» рядом с «Отпуском»). Из meta
  // убираем совпавший с заголовком префикс вида — даты и прочие детали остаются.
  const titleText = t(p.title ?? type.name)
  const groupText = t(type.name)
  const metaText = t(p.meta)
  const metaTail =
    metaText === titleText ? '' : metaText.startsWith(`${titleText} · `) ? metaText.slice(titleText.length + 3) : metaText
  const subtitle = [titleText === groupText ? '' : groupText, metaTail].filter(Boolean).join(' · ')
  // Договор — компания-сторона из формы; прочие виды — компания трудоустройства.
  const company = p.company ? t(p.company) : p.employmentCompany

  // «Сейчас ожидает»: что за этап и на ком он. «Завершено» — ТОЛЬКО для реально завершённой
  // заявки, а не когда не смогли определить ответственного.
  const wait: { label?: string; icon: string; tone?: Tone; title: string; sub?: string } = needsMe
    ? { icon: 'clock', tone: 'gold', title: l('proc.yourDecision'), sub: cur ? t(cur.act) : undefined }
    : p.status === 'done'
      ? { icon: 'check', tone: 'green', title: l('proc.finished'), sub: l('proc.allStepsDone') }
      : p.status === 'cancelled'
        ? // отменена инициатором/HR — нейтрально, не красная «Ошибка проводки»
          { icon: 'x', title: t({ ru: 'Заявка отменена', en: 'Request cancelled' }) }
        : cur
          ? {
              label: p.status === 'rejected' ? l('proc.rejectedBy') : undefined,
              icon: p.status === 'rejected' ? 'x' : cur.person || !cur.group ? 'clock' : 'users',
              tone: p.status === 'rejected' ? 'red' : undefined,
              title: t(cur.act),
              // Ответственный: живой человек (из бэкенда); иначе — группа.
              sub: cur.person?.name ?? (cur.group ? t(cur.group) : undefined),
            }
          : p.status === 'rejected'
            ? // нет открытого/отклонённого шага, но заявка не done → ошибка проводки (POST_FAILED)
              { icon: 'triangle-alert', tone: 'red', title: t({ ru: 'Ошибка проводки', en: 'Posting failed' }) }
            : // прочее без текущего шага (напр. DRAFT до reconcile) — нейтрально, НЕ «завершено»
              { icon: 'clock', title: l('proc.sPending') }
  const tnWait = wait.tone ? tone(wait.tone) : undefined

  const progress = p.approvalRouteUnavailable ? null : (
    <XStack alignItems="center" gap={8} flex={stacked ? 1 : undefined} width={stacked ? undefined : '100%'}>
      <Stack flex={1} height={6} borderRadius={4} backgroundColor="$borderSecondary" overflow="hidden">
        <Stack height={6} borderRadius={4} backgroundColor={tnStatus.solid} width={`${pct}%`} />
      </Stack>
      <Text fontSize={11} fontWeight="600" color="$muted" numberOfLines={1}>
        {l('doc.step')} {p.step} {l('doc.of')} {p.steps}
      </Text>
    </XStack>
  )

  // «Ваше решение» там, где блока «Сейчас» нет (узкое окно) — рядом со статусом.
  const decision = needsMe && !showWait ? (
    <Text fontSize={11} fontWeight="700" color={tnDecide.text} numberOfLines={1}>
      {l('proc.yourDecision')}
    </Text>
  ) : null

  return (
    <PressCard
      onPress={() => onOpen(p)}
      flexDirection="row"
      alignItems={stacked ? 'flex-start' : 'center'}
      gap={16}
      padding={16}
      flexWrap={stacked ? 'wrap' : 'nowrap'}
      {...(needsMe ? { borderColor: tnDecide.solid, borderLeftWidth: 3 } : null)}
    >
      <Stack width={44} height={44} flexShrink={0} borderRadius={7} backgroundColor={tnType.soft} alignItems="center" justifyContent="center">
        <Icon name={type.icon} size={20} color={tnType.text} />
      </Stack>

      <YStack flexGrow={1} flexShrink={1} flexBasis={0} minWidth={stacked ? 180 : 0} gap={6}>
        {/* перенос — по месту: не поместился целиком — уходит на свою строку (а не режется до
            пары букв рядом с названием); длиннее строки — многоточие */}
        <XStack alignItems="center" columnGap={10} rowGap={4} flexWrap="wrap">
          <Text fontSize={13} fontWeight="700" color="$color" flexShrink={1}>
            {titleText}
          </Text>
          {company ? (
            <XStack alignItems="center" gap={5} flexShrink={1} backgroundColor="$primaryBg" borderRadius={999} paddingHorizontal={8} paddingVertical={2}>
              <Stack flexShrink={0}>
                <Icon name="building-2" size={11} color="$primary" />
              </Stack>
              <Text fontSize={11} fontWeight="700" color="$primary" numberOfLines={1} flexShrink={1}>
                {company}
              </Text>
            </XStack>
          ) : null}
          {p.counterparty ? (
            <XStack alignItems="center" gap={7} flexShrink={1} minWidth={0}>
              <Stack flexShrink={0}>
                <Icon name="arrow-right-left" size={13} color="$tertiary" />
              </Stack>
              <XStack alignItems="baseline" gap={5} flexShrink={1} minWidth={0}>
                <Text fontSize={11} color="$muted" flexShrink={0}>
                  {l('proc.counterparty')}:
                </Text>
                <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1} flexShrink={1}>
                  {p.counterparty}
                </Text>
              </XStack>
            </XStack>
          ) : subtitle ? (
            <Text fontSize={12} color="$muted" numberOfLines={1} flexShrink={1} minWidth={0}>
              {subtitle}
            </Text>
          ) : null}
        </XStack>

        <XStack alignItems="center" columnGap={10} rowGap={4} flexWrap="wrap">
          <Text fontSize={11} color="$muted" flexShrink={0}>
            № {docNo(p)}
          </Text>
          {p.author ? (
            <XStack alignItems="center" gap={6} flexShrink={1} minWidth={0}>
              <Stack width={18} height={18} flexShrink={0} borderRadius={999} backgroundColor="$primaryBg" alignItems="center" justifyContent="center">
                <Text fontSize={8} fontWeight="700" color="$primary">
                  {initials(p.author)}
                </Text>
              </Stack>
              <Text fontSize={11} color="$muted" flexShrink={0}>
                {l('proc.author')}:
              </Text>
              {/* ФИО автора — тем же кеглем, что и название (запрос заказчика). */}
              <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1} flexShrink={1}>
                {p.author}
              </Text>
            </XStack>
          ) : null}
          {/* Кем я прихожусь документу — на любой вкладке и на Home. */}
          {rolesOf(p).map((r) => <Badge key={r}>{t(PARTICIPANT_ROLE_LABEL[r])}</Badge>)}
          {/* Подача «от имени» (роль proxy): в «Моих заявках» видно, за кого подан документ. */}
          {p.onBehalfOf ? (
            <Badge>
              {t({ ru: 'за', en: 'for' })} {p.onBehalfOf}
            </Badge>
          ) : null}
        </XStack>
      </YStack>

      {stacked ? (
        <XStack width="100%" alignItems="center" gap={11}>
          <StatusPill status={p.status} label={p.statusLabel} />
          {/* у входящей задачи прогресс всегда «1 из 1» — в тесной строке вместо него решение */}
          {decision ?? progress}
        </XStack>
      ) : (
        <XStack flexShrink={0} alignItems="center" gap={16} paddingLeft={16} borderLeftWidth={1} borderColor="$borderSecondary">
          {/* current owner — who the process waits on now */}
          {showWait && (
            <YStack width={168} gap={6}>
              <Text fontSize={10} color="$muted" textTransform="uppercase" letterSpacing={0.4}>
                {wait.label ?? l('proc.waiting')}
              </Text>
              <XStack alignItems="center" gap={8}>
                <Stack width={28} height={28} flexShrink={0} borderRadius={999} backgroundColor={tnWait?.soft ?? '$borderSecondary'} alignItems="center" justifyContent="center">
                  <Icon name={wait.icon} size={14} color={tnWait?.text ?? '$tertiary'} />
                </Stack>
                <YStack flex={1} minWidth={0}>
                  <Text fontSize={12} fontWeight={needsMe ? '700' : '600'} color={needsMe ? tnDecide.text : '$color'} numberOfLines={2}>
                    {wait.title}
                  </Text>
                  {wait.sub ? (
                    <Text fontSize={10} color="$muted" numberOfLines={1}>
                      {wait.sub}
                    </Text>
                  ) : null}
                </YStack>
              </XStack>
            </YStack>
          )}
          <YStack width={130} alignItems="flex-start" gap={8}>
            <StatusPill status={p.status} label={p.statusLabel} />
            {decision}
            {progress}
          </YStack>
          <Icon name="chevron-right" size={18} color="$tertiary" />
        </XStack>
      )}
    </PressCard>
  )
}
